import { useMemo, useRef, useState } from 'react';
import { PlusIcon, TrashIcon, CheckIcon, XMarkIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { lerValor } from '../CampoParcelas';
import { compressImagesIfNeeded } from '../../utils/imageCompression';

/**
 * Campo "Itens da solicitação".
 *
 * Nasceu no fluxo de compras (pedido do Samuel e da Pietra, 08/10/2026): a
 * solicitação com vários itens de fornecedores diferentes ia em texto corrido
 * num card só, e na aprovação ninguém sabia o que estava aprovando.
 *
 * Aqui cada linha é um item com quantidade, fornecedor e valor. A tela agrupa
 * por fornecedor, com subtotal, e na etapa de aprovação cada grupo é aprovado
 * ou reprovado separado. Quem aprovou e quando fica gravado no item.
 *
 * O valor gravado é uma lista de objetos (ItemSolicitacao). A divisão do card em
 * um card por fornecedor aprovado é feita no servidor, a partir desta lista.
 */

export type DecisaoItem = 'aprovado' | 'reprovado' | null;

/**
 * Anexo de um item (23/10/2026, pedido do André): a cotação do fornecedor (PDF, foto)
 * vai junto do item, para as etapas seguintes conferirem. Mesmo formato dos anexos
 * de campo do card; o arquivo sobe pelo mesmo /api/upload-anexo-local.
 */
export interface AnexoItem {
  name: string;
  url: string;
  type: string;
  size: number | null;
  fileName: string | null;
  uploadedAt: string | null;
  enviadoPorNome?: string | null;
}

export interface ItemSolicitacao {
  id: string;
  item: string;
  quantidade: number | null;
  unidade: string;
  fornecedor: string;
  valorUnitario: number | null;
  decisao: DecisaoItem;
  decididoEm: string | null;
  decididoPor: string | null;
  decididoPorNome: string | null;
  /** cotação/arquivos do item; cards antigos não têm (vira []) */
  anexos: AnexoItem[];
}

const normalizarAnexos = (v: any): AnexoItem[] => {
  if (!Array.isArray(v)) return [];
  return v
    .filter((a) => a && typeof a === 'object' && (a.url || a.id))
    .map((a: any) => ({
      name: String(a.name ?? a.nome ?? a.fileName ?? 'arquivo'),
      url: String(a.url ?? a.id),
      type: String(a.type ?? a.tipo ?? ''),
      size: typeof a.size === 'number' ? a.size : null,
      fileName: a.fileName || null,
      uploadedAt: a.uploadedAt || null,
      enviadoPorNome: a.enviadoPorNome || null,
    }));
};

const ehImagem = (a: AnexoItem) => /^image\//.test(a.type) || /\.(png|jpe?g|gif|webp|bmp)$/i.test(a.name || a.url);

const novoId = () => `item_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const dinheiro = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const paraEntrada = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export const SEM_FORNECEDOR = 'Sem fornecedor definido';

export const normalizarItens = (valor: any): ItemSolicitacao[] => {
  if (!Array.isArray(valor)) return [];
  return valor
    .filter((p) => p && typeof p === 'object')
    .map((p: any) => ({
      id: p.id || novoId(),
      item: String(p.item ?? p.nome ?? ''),
      quantidade: lerValor(p.quantidade),
      unidade: String(p.unidade ?? ''),
      fornecedor: String(p.fornecedor ?? '').trim(),
      valorUnitario: lerValor(p.valorUnitario ?? p.valor),
      decisao: p.decisao === 'aprovado' || p.decisao === 'reprovado' ? p.decisao : null,
      decididoEm: p.decididoEm || null,
      decididoPor: p.decididoPor || null,
      decididoPorNome: p.decididoPorNome || null,
      anexos: normalizarAnexos(p.anexos),
    }));
};

export const totalItem = (i: ItemSolicitacao) => (i.valorUnitario || 0) * (i.quantidade ?? 1);

/** Grupos por fornecedor, na ordem em que aparecem. */
export const agruparPorFornecedor = (itens: ItemSolicitacao[]) => {
  const grupos = new Map<string, ItemSolicitacao[]>();
  itens.forEach((i) => {
    const f = i.fornecedor || SEM_FORNECEDOR;
    grupos.set(f, [...(grupos.get(f) || []), i]);
  });
  return Array.from(grupos.entries()).map(([fornecedor, lista]) => ({
    fornecedor,
    itens: lista,
    subtotal: lista.reduce((s, i) => s + totalItem(i), 0),
    decisao: lista.every((i) => i.decisao === 'aprovado') ? 'aprovado' as const
      : lista.every((i) => i.decisao === 'reprovado') ? 'reprovado' as const : null,
  }));
};

/** Resumo em texto, para listas, histórico e busca. */
export const textoItens = (valor: any): string => {
  const itens = normalizarItens(valor);
  if (itens.length === 0) return '';
  const grupos = agruparPorFornecedor(itens);
  return `${itens.length} ite${itens.length === 1 ? 'm' : 'ns'} · ${grupos.length} fornecedor${grupos.length === 1 ? '' : 'es'} — `
    + grupos.map((g) => `${g.fornecedor}: ${dinheiro(g.subtotal)}${g.decisao === 'aprovado' ? ' ✓' : g.decisao === 'reprovado' ? ' ✗' : ''}`).join('; ')
    + (() => {
      const n = itens.reduce((s, i) => s + i.anexos.length, 0);
      return n ? ` · 📎 ${n} anexo${n === 1 ? '' : 's'}` : '';
    })();
};

/** Links dos anexos de um item (miniatura quando é imagem). */
export const AnexosDoItem = ({ anexos, onRemover }: { anexos: AnexoItem[]; onRemover?: (idx: number) => void }) => {
  if (!anexos.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {anexos.map((a, idx) => (
        <span key={`${a.url}-${idx}`}
          className="inline-flex items-center gap-1 max-w-[220px] bg-white border border-gray-200 rounded px-1 py-0.5">
          <a href={a.url} target="_blank" rel="noopener noreferrer" title={a.name}
            className="inline-flex items-center gap-1 min-w-0 text-[10px] text-indigo-600 hover:text-indigo-800 hover:underline">
            {ehImagem(a)
              ? <img src={a.url} alt="" className="h-5 w-5 object-cover rounded flex-shrink-0" />
              : <PaperClipIcon className="h-3 w-3 flex-shrink-0" />}
            <span className="truncate">{a.name}</span>
          </a>
          {onRemover && (
            <button type="button" onClick={() => onRemover(idx)} title="remover anexo"
              className="text-gray-300 hover:text-red-500 flex-shrink-0">
              <XMarkIcon className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
};

/** Versão só leitura, compacta, para o histórico de etapas ("cotação" que as etapas seguintes conferem). */
export const ItensResumo = ({ valor }: { valor: any }) => {
  const itens = normalizarItens(valor);
  if (!itens.length) return <span className="text-[10px] text-gray-400">—</span>;
  const grupos = agruparPorFornecedor(itens);
  return (
    <div className="space-y-1 w-full">
      {grupos.map((g) => (
        <div key={g.fornecedor} className="rounded border border-gray-200 bg-white">
          <div className="flex items-center gap-2 px-1.5 py-0.5 border-b border-gray-100">
            <span className="text-[10px] font-semibold text-gray-800 flex-1 min-w-0 truncate">{g.fornecedor}</span>
            <span className="text-[10px] text-gray-700 tabular-nums">{dinheiro(g.subtotal)}</span>
            {g.decisao && (
              <span className={`text-[9px] font-semibold ${g.decisao === 'aprovado' ? 'text-emerald-700' : 'text-red-700'}`}>
                {g.decisao === 'aprovado' ? 'APROVADO' : 'REPROVADO'}
              </span>
            )}
          </div>
          {g.itens.map((i) => (
            <div key={i.id} className="px-1.5 py-0.5">
              <div className="flex items-center gap-1.5">
                <span className="flex-1 min-w-0 text-[10px] text-gray-800">{i.item}</span>
                <span className="text-[10px] text-gray-500 tabular-nums">
                  {i.quantidade ?? 1}{i.unidade ? ` ${i.unidade}` : ''}
                  {i.valorUnitario !== null ? ` × ${dinheiro(i.valorUnitario)}` : ''}
                </span>
              </div>
              <AnexosDoItem anexos={i.anexos} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/** Para onde vão os arquivos (mesma pasta dos anexos do card). */
export interface ContextoUploadItens {
  flowId?: string;
  cardId?: string;
  campoId?: string;
  flowNome?: string;
  cardTitulo?: string;
}

const MAX_ANEXO_MB = 10;

interface Props {
  valor: any;
  onChange: (novo: ItemSolicitacao[]) => void;
  /** incluir, mudar e remover itens (quem preenche a solicitação) */
  podeEditarLista: boolean;
  /** aprovar ou reprovar por fornecedor (etapa de aprovação) */
  podeAprovar: boolean;
  usuario?: { id?: string; nome?: string } | null;
  /** anexar/remover arquivos nos itens (padrão: igual a podeEditarLista) */
  podeAnexar?: boolean;
  contextoUpload?: ContextoUploadItens;
}

const vazio = { item: '', quantidade: '', unidade: '', fornecedor: '', valor: '' };

const CampoItens = ({ valor, onChange, podeEditarLista, podeAprovar, usuario, podeAnexar, contextoUpload }: Props) => {
  const anexar = podeAnexar ?? podeEditarLista;
  const inputArquivo = useRef<HTMLInputElement>(null);
  const alvoUpload = useRef<string | null>(null);
  const [enviandoId, setEnviandoId] = useState<string | null>(null);
  const itens = useMemo(() => normalizarItens(valor), [valor]);
  const grupos = useMemo(() => agruparPorFornecedor(itens), [itens]);
  const [novo, setNovo] = useState(vazio);
  // 23/10/2026 (André): clipe também na linha de digitação; os anexos vão junto ao clicar em Adicionar
  const NOVO_ID = '__novo__';
  const [novoAnexos, setNovoAnexos] = useState<AnexoItem[]>([]);
  const fornecedoresConhecidos = useMemo(
    () => Array.from(new Set(itens.map((i) => i.fornecedor).filter(Boolean))), [itens]);

  const trocar = (id: string, mudanca: Partial<ItemSolicitacao>) =>
    onChange(itens.map((i) => (i.id === id ? { ...i, ...mudanca } : i)));

  const decidirGrupo = (fornecedor: string, decisao: DecisaoItem) => {
    if (!podeAprovar) return;
    const carimbo = decisao
      ? { decisao, decididoEm: new Date().toISOString(), decididoPor: usuario?.id || null, decididoPorNome: usuario?.nome || null }
      : { decisao: null, decididoEm: null, decididoPor: null, decididoPorNome: null };
    onChange(itens.map((i) => ((i.fornecedor || SEM_FORNECEDOR) === fornecedor ? { ...i, ...carimbo } : i)));
  };

  const adicionar = () => {
    const nome = novo.item.trim();
    if (!nome) return;
    onChange([...itens, {
      id: novoId(), item: nome, quantidade: lerValor(novo.quantidade), unidade: novo.unidade.trim(),
      fornecedor: novo.fornecedor.trim(), valorUnitario: lerValor(novo.valor),
      decisao: null, decididoEm: null, decididoPor: null, decididoPorNome: null, anexos: novoAnexos,
    }]);
    setNovoAnexos([]);
    // fornecedor fica preenchido: quase sempre o próximo item é do mesmo
    setNovo({ ...vazio, fornecedor: novo.fornecedor });
  };

  const escolherArquivo = (itemId: string) => {
    if (enviandoId) { alert('⏳ Aguarde o envio em andamento terminar.'); return; }
    alvoUpload.current = itemId;
    inputArquivo.current?.click();
  };

  const enviarArquivos = async (lista: File[]) => {
    const itemId = alvoUpload.current;
    if (!itemId || lista.length === 0) return;
    const grandes = lista.filter((f) => f.size > MAX_ANEXO_MB * 1024 * 1024);
    if (grandes.length) {
      alert(`⚠️ Arquivo muito grande (máx. ${MAX_ANEXO_MB}MB): ${grandes.map((f) => f.name).join(', ')}`);
      if (inputArquivo.current) inputArquivo.current.value = '';
      return;
    }
    setEnviandoId(itemId);
    try {
      const arquivos = await compressImagesIfNeeded(lista);
      const enviados = await Promise.all(arquivos.map(async (file): Promise<AnexoItem | null> => {
        try {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('flowId', contextoUpload?.flowId || 'geral');
          fd.append('cardId', contextoUpload?.cardId || `novo_${Date.now()}`);
          fd.append('campoId', contextoUpload?.campoId || 'itens');
          fd.append('flowNome', contextoUpload?.flowNome || 'Geral');
          fd.append('cardTitulo', contextoUpload?.cardTitulo || 'Novo Card');
          const r = await fetch('/api/upload-anexo-local', { method: 'POST', body: fd });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const res = await r.json();
          if (!res.url) throw new Error(res.error || 'sem url');
          return {
            name: res.originalName || file.name, url: res.url, type: res.type || file.type,
            size: res.size ?? file.size, fileName: res.fileName || null, uploadedAt: res.uploadedAt || new Date().toISOString(),
            enviadoPorNome: usuario?.nome || null,
          };
        } catch (err) {
          console.error('Erro no upload do anexo do item:', err);
          alert(`Erro ao enviar ${file.name}`);
          return null;
        }
      }));
      const ok = enviados.filter((a): a is AnexoItem => a !== null);
      if (ok.length && itemId === NOVO_ID) {
        setNovoAnexos((prev) => [...prev, ...ok]);
      } else if (ok.length) {
        onChange(itens.map((i) => (i.id === itemId ? { ...i, anexos: [...i.anexos, ...ok] } : i)));
      }
    } finally {
      setEnviandoId(null);
      alvoUpload.current = null;
      if (inputArquivo.current) inputArquivo.current.value = '';
    }
  };

  const removerAnexo = (itemId: string, idx: number) =>
    onChange(itens.map((i) => (i.id === itemId ? { ...i, anexos: i.anexos.filter((_, k) => k !== idx) } : i)));

  const total = itens.reduce((s, i) => s + totalItem(i), 0);
  const listaId = useMemo(() => `fornecedores_${Math.random().toString(36).slice(2, 8)}`, []);
  const inputCls = 'bg-white text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-gray-400';

  return (
    <div className="p-2.5 bg-gray-50/80 rounded-lg border border-gray-100 space-y-2">
      {anexar && (
        <input ref={inputArquivo} type="file" multiple className="hidden"
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar,.eml,.msg"
          onChange={(e) => enviarArquivos(Array.from(e.target.files || []))} />
      )}
      <datalist id={listaId}>
        {fornecedoresConhecidos.map((f) => <option key={f} value={f} />)}
      </datalist>

      {itens.length === 0 && (
        <p className="text-xs text-gray-400 px-1">
          {podeEditarLista ? 'Nenhum item ainda. Adicione abaixo, um por linha, com o fornecedor de cada um.' : 'Nenhum item informado.'}
        </p>
      )}

      {grupos.map((g) => (
        <div key={g.fornecedor}
          className={`rounded-md border ${g.decisao === 'aprovado' ? 'border-emerald-200 bg-emerald-50/50'
            : g.decisao === 'reprovado' ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-100">
            <span className={`text-xs font-semibold flex-1 min-w-0 truncate ${g.fornecedor === SEM_FORNECEDOR ? 'text-amber-700' : 'text-gray-800'}`}>
              {g.fornecedor}
            </span>
            <span className="text-xs text-gray-700 tabular-nums">{dinheiro(g.subtotal)}</span>
            {podeAprovar ? (
              <div className="flex items-center gap-1">
                <button type="button" title="aprovar este fornecedor"
                  onClick={() => decidirGrupo(g.fornecedor, g.decisao === 'aprovado' ? null : 'aprovado')}
                  className={`px-1.5 py-0.5 rounded text-[11px] flex items-center gap-0.5 border
                    ${g.decisao === 'aprovado' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'}`}>
                  <CheckIcon className="h-3 w-3" strokeWidth={3} /> Aprovar
                </button>
                <button type="button" title="reprovar este fornecedor"
                  onClick={() => decidirGrupo(g.fornecedor, g.decisao === 'reprovado' ? null : 'reprovado')}
                  className={`px-1.5 py-0.5 rounded text-[11px] flex items-center gap-0.5 border
                    ${g.decisao === 'reprovado' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-300 hover:bg-red-50'}`}>
                  <XMarkIcon className="h-3 w-3" strokeWidth={3} /> Reprovar
                </button>
              </div>
            ) : g.decisao && (
              <span className={`text-[10px] font-semibold ${g.decisao === 'aprovado' ? 'text-emerald-700' : 'text-red-700'}`}>
                {g.decisao === 'aprovado' ? 'APROVADO' : 'REPROVADO'}
              </span>
            )}
          </div>

          {g.itens.map((i) => {
            const botaoClipe = anexar && (
              <button type="button" onClick={() => escolherArquivo(i.id)} disabled={!!enviandoId}
                title="anexar cotação / arquivo neste item"
                className={`flex-shrink-0 ${enviandoId === i.id ? 'text-indigo-500 animate-pulse' : i.anexos.length ? 'text-indigo-500 hover:text-indigo-700' : 'text-gray-300 hover:text-gray-600'} disabled:cursor-wait`}>
                <PaperClipIcon className="h-3.5 w-3.5" />
              </button>
            );
            return (
            <div key={i.id} className="px-2 py-1 space-y-1">
            <div className="flex items-center gap-1.5">
              {podeEditarLista ? (
                <>
                  <input value={i.item} onChange={(e) => trocar(i.id, { item: e.target.value })}
                    className={`flex-1 min-w-0 ${inputCls}`} />
                  <input key={`q-${i.id}-${i.quantidade ?? ''}`} defaultValue={i.quantidade === null ? '' : paraEntrada(i.quantidade)}
                    onBlur={(e) => { const q = lerValor(e.target.value); if (q !== i.quantidade) trocar(i.id, { quantidade: q }); }}
                    placeholder="Qtd" inputMode="decimal" className={`w-12 text-right ${inputCls}`} />
                  <input value={i.unidade} onChange={(e) => trocar(i.id, { unidade: e.target.value })}
                    placeholder="un" className={`w-10 ${inputCls}`} />
                  <input value={i.fornecedor} list={listaId} onChange={(e) => trocar(i.id, { fornecedor: e.target.value })}
                    placeholder="Fornecedor" className={`w-32 ${inputCls}`} />
                  <input key={`v-${i.id}-${i.valorUnitario ?? ''}`} defaultValue={i.valorUnitario === null ? '' : paraEntrada(i.valorUnitario)}
                    onBlur={(e) => { const v = lerValor(e.target.value); if (v !== i.valorUnitario) trocar(i.id, { valorUnitario: v }); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
                    placeholder="R$ unit." inputMode="decimal" className={`w-20 text-right ${inputCls}`} />
                  {botaoClipe}
                  <button type="button" onClick={() => onChange(itens.filter((x) => x.id !== i.id))}
                    title="remover item" className="text-gray-300 hover:text-red-500 flex-shrink-0">
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 min-w-0 text-xs text-gray-800">{i.item}</span>
                  <span className="text-xs text-gray-500 tabular-nums">
                    {i.quantidade ?? 1}{i.unidade ? ` ${i.unidade}` : ''}
                    {i.valorUnitario !== null ? ` × ${dinheiro(i.valorUnitario)}` : ''}
                  </span>
                  <span className="w-24 text-right text-xs text-gray-700 tabular-nums">{dinheiro(totalItem(i))}</span>
                  {botaoClipe}
                </>
              )}
            </div>
            {enviandoId === i.id && <p className="text-[10px] text-indigo-600 animate-pulse">📤 Enviando arquivo...</p>}
            <AnexosDoItem anexos={i.anexos} onRemover={anexar ? (idx) => removerAnexo(i.id, idx) : undefined} />
            </div>
            );
          })}

          {g.decisao && g.itens[0]?.decididoPorNome && (
            <p className="px-2 pb-1 text-[10px] text-gray-500">
              {g.decisao === 'aprovado' ? 'aprovado' : 'reprovado'} por {g.itens[0].decididoPorNome}
              {g.itens[0].decididoEm ? ` em ${new Date(g.itens[0].decididoEm).toLocaleDateString('pt-BR')}` : ''}
            </p>
          )}
        </div>
      ))}

      {podeEditarLista && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <input value={novo.item} onChange={(e) => setNovo({ ...novo, item: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionar(); } }}
            placeholder="Item (ex.: Cabo PP 3x2,5mm)" className={`flex-1 min-w-[140px] ${inputCls}`} />
          <input value={novo.quantidade} onChange={(e) => setNovo({ ...novo, quantidade: e.target.value })}
            placeholder="Qtd" inputMode="decimal" className={`w-12 ${inputCls}`} />
          <input value={novo.unidade} onChange={(e) => setNovo({ ...novo, unidade: e.target.value })}
            placeholder="un" className={`w-10 ${inputCls}`} />
          <input value={novo.fornecedor} list={listaId} onChange={(e) => setNovo({ ...novo, fornecedor: e.target.value })}
            placeholder="Fornecedor" className={`w-32 ${inputCls}`} />
          <input value={novo.valor} onChange={(e) => setNovo({ ...novo, valor: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionar(); } }}
            placeholder="R$ unit." inputMode="decimal" className={`w-20 ${inputCls}`} />
          {anexar && (
            <button type="button" onClick={() => escolherArquivo(NOVO_ID)} disabled={!!enviandoId}
              title="anexar cotação / arquivo neste item (vai junto ao adicionar)"
              className={`flex-shrink-0 ${enviandoId === NOVO_ID ? 'text-indigo-500 animate-pulse' : novoAnexos.length ? 'text-indigo-500 hover:text-indigo-700' : 'text-gray-400 hover:text-gray-700'} disabled:cursor-wait`}>
              <PaperClipIcon className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={adicionar} disabled={!novo.item.trim()}
            className="px-2 py-1 bg-gray-800 text-white rounded text-xs flex items-center gap-1 disabled:opacity-40">
            <PlusIcon className="h-3 w-3" /> Adicionar
          </button>
          {(enviandoId === NOVO_ID || novoAnexos.length > 0) && (
            <div className="w-full space-y-1">
              {enviandoId === NOVO_ID && <p className="text-[10px] text-indigo-600 animate-pulse">📤 Enviando arquivo...</p>}
              <AnexosDoItem anexos={novoAnexos} onRemover={(idx) => setNovoAnexos((prev) => prev.filter((_, k) => k !== idx))} />
            </div>
          )}
        </div>
      )}

      {itens.length > 0 && (
        <div className="flex items-center justify-between px-1 pt-1 text-[11px] text-gray-600">
          <span>{itens.length} ite{itens.length === 1 ? 'm' : 'ns'} · {grupos.length} fornecedor{grupos.length === 1 ? '' : 'es'}
            {grupos.length > 1 ? ' · cada fornecedor vira um card próprio depois da aprovação' : ''}</span>
          <span className="font-semibold tabular-nums">{dinheiro(total)}</span>
        </div>
      )}
    </div>
  );
};

export default CampoItens;
